// @ts-nocheck
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MdPhotoCamera } from "react-icons/md";
import useCreateProgram from "hooks/programs/useCreateProgram";
import useFields from "hooks/fields/useFields";
import useLocations from "hooks/locations/useLocations";
import usePresignedUpload from "hooks/storage/usePresignedUpload";
import { useToast } from "context/ToastContext";
import InputField from "components/form/InputField";
import SelectField from "components/form/SelectField";
import TextareaField from "components/form/TextareaField";
import RichTextEditor from "components/form/RichTextEditor";
import ToggleInput from "components/form/toggle/ToggleInput";

const ThumbnailUpload = ({ value, onChange }) => {
  const { upload, uploading, progress, error } = usePresignedUpload();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const result = await upload(file, { folder: "programs/thumbnails", file_type: "image" });
    if (result) onChange(result.public_url);
  };

  return (
    <div>
      <label className="block text-xs font-semibold text-navy-700 mb-2">Thumbnail</label>
      {value ? (
        <div className="relative group rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
          <img src={value} alt="Thumbnail" className="w-full h-40 object-cover" />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition"
          >
            <MdPhotoCamera size={24} className="text-white" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full h-32 rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center gap-2 text-slate-400 hover:border-navy-300 hover:text-navy-500 transition bg-slate-50"
        >
          <MdPhotoCamera size={22} />
          <span className="text-xs font-medium">Upload thumbnail</span>
          <span className="text-[10px] text-slate-300">JPG, PNG, WebP</span>
        </button>
      )}
      {uploading && (
        <div className="mt-2 space-y-1">
          <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-navy-500 transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-xs text-slate-400 text-center">Uploading {progress}%</p>
        </div>
      )}
      {value && !uploading && (
        <div className="flex gap-3 mt-2">
          <button type="button" onClick={() => inputRef.current?.click()}
            className="text-xs text-navy-600 hover:text-navy-800 font-medium transition">
            Change
          </button>
          <button type="button" onClick={() => onChange("")}
            className="text-xs text-red-500 hover:text-red-700 transition">
            Remove
          </button>
        </div>
      )}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp"
        className="hidden" onChange={handleChange} />
    </div>
  );
};

const TYPE_OPTIONS = [
  { value: "course",     label: "Training Course" },
  { value: "diploma",    label: "Training Diploma" },
  { value: "contracted", label: "Contracted Course" },
];
const MODE_OPTIONS = [
  { value: "online",  label: "Online" },
  { value: "offline", label: "Offline" },
  { value: "hybrid",  label: "Hybrid" },
];
const CURRENCY_OPTIONS = [
  { value: "MYR", label: "MYR — Malaysian Ringgit" },
  { value: "USD", label: "USD — US Dollar" },
  { value: "EUR", label: "EUR — Euro" },
  { value: "GBP", label: "GBP — British Pound" },
  { value: "AED", label: "AED — UAE Dirham" },
  { value: "SAR", label: "SAR — Saudi Riyal" },
];

const ProgramCreatePage = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { createProgram, loading, error, fieldErrors } = useCreateProgram();

  const { fields }    = useFields({ is_active: true });
  const { locations } = useLocations({ is_active: true });

  const fieldOptions    = fields.map((f) => ({ value: f.uid, label: f.name }));
  const locationOptions = locations.map((l) => ({ value: l.uid, label: `${l.name} — ${l.city}, ${l.country}` }));

  const [form, setForm] = useState({
    name:             "",
    description:      "",
    objectives:       "",
    target_audience:  "",
    prerequisites:    "",
    program_type:     "course",
    field:            "",
    location:         "",
    duration:         "",
    level:            "beginner",
    mode:             "offline",
    language:         "English",
    start_date:       "",
    end_date:         "",
    max_participants: "10",
    contact_email:    "",
    contact_phone:    "",
    status:           "upcoming",
    is_active:        true,
    price:            "",
    currency:         "MYR",
    thumbnail:        "",
  });

  const updateFormData = (key: string, value: any) =>
    setForm((p) => ({ ...p, [key]: value }));

  const isFormValid =
    form.name.trim() !== "" &&
    form.description.trim() !== "" &&
    form.objectives.trim() !== "" &&
    form.target_audience.trim() !== "" &&
    form.field !== "" &&
    form.location !== "" &&
    form.contact_email.trim() !== "" &&
    form.contact_phone.trim() !== "" &&
    form.start_date.trim() !== "" &&
    form.end_date.trim() !== "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { field, location, ...rest } = form;
    const created = await createProgram({
      ...rest,
      field_uid:        field || undefined,
      location_uid:     location || undefined,
      trainer_uids:     [],
      max_participants: form.max_participants ? Number(form.max_participants) : null,
    });
    if (created) {
      addToast("Program created successfully", "success");
      navigate("/admin/programs");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm max-w-5xl mx-auto">
      <div className="px-6 py-4 border-b border-slate-100">
        <h1 className="text-base font-bold text-navy-800">Create Program</h1>
        <p className="text-xs text-slate-400 mt-0.5">Fill in the details to add a new training program</p>
      </div>

      <form onSubmit={handleSubmit} className="px-6 py-5">
        {error && (
          <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">

          {/* Program name — full width */}
          <div className="md:col-span-2">
            <InputField
              label="Program Name"
              field="name"
              placeholder="e.g. Advanced Leadership Diploma"
              formData={form}
              errors={fieldErrors}
              updateFormData={updateFormData}
            />
          </div>

          {/* Type — full width */}
          <div className="md:col-span-2">
            <SelectField
              label="Program Type"
              field="program_type"
              options={TYPE_OPTIONS}
              formData={form}
              errors={fieldErrors}
              updateFormData={updateFormData}
            />
          </div>

          {/* Field + Location */}
          <SelectField
            label="Field"
            field="field"
            required
            options={fieldOptions}
            formData={form}
            errors={fieldErrors}
            updateFormData={updateFormData}
          />
          <SelectField
            label="Location"
            field="location"
            required
            options={locationOptions}
            formData={form}
            errors={fieldErrors}
            updateFormData={updateFormData}
          />

          {/* Duration + Language */}
          <InputField
            label="Duration"
            field="duration"
            placeholder="e.g. 10 Consecutive Days"
            formData={form}
            errors={fieldErrors}
            updateFormData={updateFormData}
          />
          <InputField
            label="Language"
            field="language"
            placeholder="e.g. English"
            formData={form}
            errors={fieldErrors}
            updateFormData={updateFormData}
          />

          {/* Level + Mode */}
          <SelectField
            label="Mode"
            field="mode"
            options={MODE_OPTIONS}
            formData={form}
            errors={fieldErrors}
            updateFormData={updateFormData}
          />

          {/* Start Date + End Date */}
          <InputField
            label="Start Date"
            field="start_date"
            type="date"
            required={true}
            formData={form}
            errors={fieldErrors}
            updateFormData={updateFormData}
          />
          <InputField
            label="End Date"
            field="end_date"
            type="date"
            required={true}
            formData={form}
            errors={fieldErrors}
            updateFormData={updateFormData}
          />

          {/* Max Participants + Status */}

          {/* Price + Currency */}
          <InputField
            label="Price"
            field="price"
            type="text"
            required={false}
            placeholder="e.g. 1500.00"
            formData={form}
            errors={fieldErrors}
            updateFormData={updateFormData}
          />
          <SelectField
            label="Currency"
            field="currency"
            options={CURRENCY_OPTIONS}
            formData={form}
            errors={fieldErrors}
            updateFormData={updateFormData}
          />

          {/* Contact Email + Phone */}
          <InputField
            label="Contact Email"
            field="contact_email"
            type="email"
            required={true}
            placeholder="info@ika-edu.com"
            formData={form}
            errors={fieldErrors}
            updateFormData={updateFormData}
          />
          <InputField
            label="Contact Phone"
            field="contact_phone"
            type="tel"
            required={true}
            placeholder="+601139936766"
            formData={form}
            errors={fieldErrors}
            updateFormData={updateFormData}
          />

          {/* Description — full width */}
          <div className="md:col-span-2">
            <RichTextEditor
              label="Description"
              field="description"
              required={true}
              placeholder="Detailed description of the program content..."
              formData={form}
              errors={fieldErrors}
              updateFormData={updateFormData}
            />
          </div>

          {/* Objectives — full width */}
          <div className="md:col-span-2">
            <RichTextEditor
              label="Objectives"
              field="objectives"
              required={true}
              placeholder="What participants will achieve..."
              formData={form}
              errors={fieldErrors}
              updateFormData={updateFormData}
            />
          </div>

          {/* Target Audience + Prerequisites */}
          <TextareaField
            label="Target Audience"
            field="target_audience"
            required={true}
            rows={3}
            placeholder="Who this program is for..."
            formData={form}
            errors={fieldErrors}
            updateFormData={updateFormData}
          />
          <TextareaField
            label="Prerequisites"
            field="prerequisites"
            required={false}
            rows={3}
            placeholder="Requirements before joining..."
            formData={form}
            errors={fieldErrors}
            updateFormData={updateFormData}
          />

          {/* Thumbnail — full width */}
          <div className="md:col-span-2">
            <ThumbnailUpload
              value={form.thumbnail}
              onChange={(url) => updateFormData("thumbnail", url)}
            />
          </div>

          {/* Active toggle — full width */}
          <div className="md:col-span-2">
            <ToggleInput
              label="Active"
              field="is_active"
              formData={form}
              errors={fieldErrors}
              updateFormData={updateFormData}
            />
          </div>

        </div>

        {/* Actions */}
        <div className="flex gap-2 border-t border-slate-100 pt-4 mt-2">
          <button
            type="button"
            onClick={() => navigate("/admin/programs")}
            className="flex-1 rounded-md lg:rounded-lg border border-slate-200 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !isFormValid}
            className="flex-1 rounded-md lg:rounded-lg bg-navy-800 py-2.5 text-sm font-semibold text-white transition enabled:hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Creating..." : "Create Program"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProgramCreatePage;
